import React, { Component } from 'react';
import { connect } from 'react-redux';
import { redirect } from 'redux-first-router';
import { TransitionGroup, Transition } from 'transition-group';
import * as ReactGA from 'react-ga';

import {
  AppGalleryPage,
  ClockGalleryPage,
  AppPage,
  PermissionPage,
  Collection,
  Outdated,
  Search
} from 'pages';
import { isAppPayload, getV2Route } from 'utils';
import {
  androidSlideDuration,
  androidTransitionDuration,
  iOSSlideDuration,
  iOSTransitionDuration
} from 'themes';
import { isiOS } from 'config';
import { PageRoutes } from './constants';
import MinePage from './pages/v2/MinePage';
import AppDetailsPage from './pages/v2/AppDetailsPage';
import CollectionPage from './pages/v2/CollectionPage';
import GalleryPage from './pages/v2/GalleryPage';
import SearchPage from './pages/v2/SearchPage';
import PermissionsPage from './pages/v2/PermissionsPage';
import ManageTilesPage from './pages/v2/ManageTilesPage';
import InstalledApps from './pages/v2/InstalledApps';
import InstalledClocks from './pages/v2/InstalledClocks';
import InstalledTiles from './pages/v2/InstalledTiles';
import NotFoundPage from './pages/v2/NotFoundPage';

const getTransitionDuration = (direction) => {
  if (direction === 'slide-left' || direction === 'slide-right') {
    return isiOS ? iOSSlideDuration : androidSlideDuration;
  }

  return isiOS ? iOSTransitionDuration : androidTransitionDuration;
};

class TransitionRoute extends Component {
  /*
   * This is to prevent this rendering multiple times
   * when properties change but it is the same view
   *
   * The TransitionGroup would either have weird visual bugs
   * when rendering multiple times or delay the whole duration
   * before triggering the animation due to multiple renders
   * of the same named view
   */
  shouldComponentUpdate({ name }) {
    return name !== this.props.name;
  }

  render() {
    const { children, name, direction } = this.props;

    return (
      <TransitionGroup
        className="transition"
        component="div"
        duration={getTransitionDuration(direction)}
        prefix="slide"
      >
        <Transition key={name}>{children}</Transition>
      </TransitionGroup>
    );
  }
}

const Routes = ({ location, showGalleryV2, redirectToV2 }) => {
  const isApp = isAppPayload(location.payload);
  let route = location.type;

  if (showGalleryV2) {
    route = getV2Route(location);
    if (route !== location.type) {
      redirectToV2(route, location.payload);
      return null;
    }
  }

  switch (location.type) {
    case PageRoutes.MY_APPS:
    case PageRoutes.GALLERY:
      const OldGalleryPage = isApp ? AppGalleryPage : ClockGalleryPage;
      const showAll = location.type === PageRoutes.GALLERY;
      return <OldGalleryPage payload={location.payload} showAll={showAll} />;
    case PageRoutes.SEARCH:
      return <Search payload={location.payload} isApp={isApp} />;
    case PageRoutes.COLLECTION:
      return <Collection payload={location.payload} isApp={isApp} />;
    case PageRoutes.APP:
      return <AppPage payload={location.payload} />;
    case PageRoutes.PERMISSION:
      return <PermissionPage payload={location.payload} />;
    case PageRoutes.PERMISSION_V2:
      return <PermissionsPage payload={location.payload} />;
    case PageRoutes.OUTDATED:
      return <Outdated />;
    case PageRoutes.GALLERY_V2_CLOCKS:
      return <InstalledClocks />;
    case PageRoutes.GALLERY_V2_TILES:
      return <InstalledTiles />;
    case PageRoutes.DISCOVER_V2_APPS:
    case PageRoutes.DISCOVER_V2_CLOCKS:
    case PageRoutes.DISCOVER_V2_TILES:
      return <GalleryPage />;
    case PageRoutes.GALLERY_V2_APPS:
      return <InstalledApps />;
    case PageRoutes.MINE_V2:
      return <MinePage />;
    case PageRoutes.COLLECTION_V2:
      return <CollectionPage id={location.payload.id} />;
    case PageRoutes.APP_V2:
      return <AppDetailsPage payload={location.payload} />;
    case PageRoutes.TILE_V2:
      return <AppDetailsPage payload={location.payload} />;
    case PageRoutes.MY_TILES_V2:
      return <ManageTilesPage />;
    case PageRoutes.SEARCH_V2:
    case PageRoutes.SEARCH_ITEMS_V2:
      return <SearchPage />;
    default:
      return <NotFoundPage />;
  }
};

const mapStateToProps = ({
  location,
  gallery: {
    direction,
    experiments: { galleryV2 }
  }
}) => ({
  location,
  direction: direction && direction.toLowerCase(),
  showGalleryV2: galleryV2
});

const mapDispatchToProps = (dispatch) => ({
  redirectToV2: (type, payload) => {
    dispatch(
      redirect({
        type,
        payload
      })
    );
  }
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)((props) => {
  ReactGA.pageview(
    window.location.pathname + window.location.hash + window.location.search
  );
  // for gallery v2 dont display any animations or transition routes yet
  if (props.showGalleryV2) {
    return <Routes {...props} />;
  }

  return (
    <div className={props.direction}>
      <TransitionRoute name={props.location.type} direction={props.direction}>
        <Routes {...props} />
      </TransitionRoute>
    </div>
  );
});
